import React from 'react';
import { RepeatIcon, FileIcon, FilesIcon } from 'lucide-react';

interface EditRecurringInstanceModalProps {
  onClose: () => void;
  onEditThisOnly: () => void;
  onEditAllFuture: () => void;
  reminderTitle: string;
}

/**
 * Modal that asks the user whether they want to edit just this instance
 * or all future instances of a recurring reminder
 */
export function EditRecurringInstanceModal({
  onClose,
  onEditThisOnly,
  onEditAllFuture,
  reminderTitle,
}: EditRecurringInstanceModalProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
              <RepeatIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Edit Recurring Reminder
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                "{reminderTitle}"
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-700">
            This is a recurring reminder. What would you like to edit?
          </p>

          {/* Option 1: Edit This Only */}
          <button
            onClick={onEditThisOnly}
            className="w-full flex items-start gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
          >
            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-100 group-hover:bg-blue-100 flex items-center justify-center transition-colors">
              <FileIcon className="h-5 w-5 text-gray-600 group-hover:text-blue-600 transition-colors" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 group-hover:text-blue-900">
                This reminder only
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Edit only this occurrence. Future reminders will remain unchanged.
              </p>
            </div>
          </button>

          {/* Option 2: Edit All Future */}
          <button
            onClick={onEditAllFuture}
            className="w-full flex items-start gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
          >
            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-100 group-hover:bg-blue-100 flex items-center justify-center transition-colors">
              <FilesIcon className="h-5 w-5 text-gray-600 group-hover:text-blue-600 transition-colors" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 group-hover:text-blue-900">
                All future reminders
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Update the recurring pattern. This will affect all future occurrences.
              </p>
            </div>
          </button>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
